import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { CreditKeyConfirmDialog } from "./CreditKeyConfirmDialog";

type Review = {
  environment?: "sandbox" | "production";
  test: boolean;
  amount: number;
  shippingAmount: number;
  subtotal: number;
  holdDays?: number;
  isLocalPickup?: boolean;
  approved: boolean;
  canApprove: boolean;
  disabledReason?: string;
  snapshot: string;
};

/** Mounted only inside the admin-gated order panel. Review does not grant access. */
export function CreditKeyOrderReview({ orderId }: { orderId: string }) {
  const review = useQuery(api.creditKey.getOrderReview, { orderId }) as Review | null | undefined;
  const approve = useMutation(api.creditKey.approveOrderSnapshot);
  const [selectedReview, setSelectedReview] = React.useState<Review | null>(null);
  const id = React.useId();

  if (review === undefined) return <p role="status" className="text-sm text-muted-foreground">Loading financing order review…</p>;
  if (review === null) return null;
  if ([review.amount, review.subtotal, review.shippingAmount].some((amount) =>
    typeof amount !== "number" || !Number.isFinite(amount) || amount < 0 || Number(amount.toFixed(2)) !== amount
  ) || review.amount <= 0) throw new Error("Verified dollar totals are required before staff order review");
  const canApprove = review.canApprove && !review.approved && (review.environment === "production" || (review.environment === "sandbox" && review.test));

  async function approveReview(reason: string) {
    if (!selectedReview || !canApprove) throw new Error("Order review is no longer available");
    try {
      await approve({ orderId, expectedSnapshot: selectedReview.snapshot, reason });
      toast.success("Order snapshot approved for financing. No payment has been authorized or captured.");
    } catch {
      toast.error("Order review could not be saved. Refresh the pricing and shipping details before approving again.", { duration: Infinity });
      throw new Error("Order review was not saved");
    }
  }

  return (
    <Card className="mb-4" role="region" aria-labelledby={`${id}-title`}>
      <CardHeader>
        <h2 id={`${id}-title`} className="font-semibold">Credit Key order review</h2>
        <CardDescription>
          {review.isLocalPickup ? "Independently verify the invoice, pickup warehouse, zero delivery charge, and pickup or customer-storage selection before approving this exact order snapshot." : "Independently verify the invoice, carrier quote, delivery details, and the ship-now or customer-storage selection before approving this exact order snapshot."} Approval does not enable customer access or charge a payment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Badge variant="outline">{review.approved ? "Current snapshot approved" : "Staff review required"}</Badge>
          <Badge variant="outline">{review.environment === "sandbox" ? "Sandbox" : review.environment === "production" ? "Production" : "Environment not configured"}</Badge>
          <Badge variant="outline">{review.test ? "Designated test order" : "Not a test order"}</Badge>
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm">
          <dt>Coffee subtotal</dt><dd className="text-end tabular-nums">{formatCurrency(review.subtotal)}</dd>
          <dt>{review.isLocalPickup ? "Local pickup · no delivery charge" : "Shipping amount to verify"}</dt><dd className="text-end tabular-nums">{formatCurrency(review.shippingAmount)}</dd>
          <dt>Fulfillment</dt><dd className="text-end">{review.holdDays ? `Hold for customer · ${review.holdDays} days` : review.isLocalPickup ? "Pick up when ready" : "Ship now"}</dd>
          <dt>Total to finance</dt><dd className="text-end font-semibold tabular-nums">{formatCurrency(review.amount)}</dd>
        </dl>
        <p className="text-sm text-muted-foreground">Changing pricing, shipping, or order items invalidates this approval. Never designate a real purchase as a test to bypass the sandbox restriction.</p>
        {review.disabledReason ? <p className="text-sm text-muted-foreground">{review.disabledReason}</p> : null}
        {!review.approved ? <Button type="button" variant="outline" disabled={!canApprove} onClick={() => setSelectedReview({ ...review })}>Review financing order</Button> : null}
      </CardContent>
      {selectedReview ? (
        <CreditKeyConfirmDialog
          title="Approve this exact financing order?"
          confirmLabel="Approve reviewed order"
          description={`I independently verified the ${formatCurrency(selectedReview.subtotal)} coffee subtotal, ${selectedReview.isLocalPickup ? "pickup warehouse with no delivery charge" : `${formatCurrency(selectedReview.shippingAmount)} carrier quote and shipping amount`}, and ${formatCurrency(selectedReview.amount)} total shown on this invoice. ${selectedReview.holdDays ? `All financed bags will be paid and held for ${selectedReview.holdDays} days, with 90 days of free storage.` : selectedReview.isLocalPickup ? "All financed bags will be prepared for pickup." : "All financed bags ship now."} ${selectedReview.environment === "sandbox" ? "This is a designated test order, not a real customer purchase." : "This is a production order and requires the approved production workflow."} This approval does not authorize financing or mark coffee shipped.`}
          failureMessage="Order review could not be saved. Close this dialog, refresh the invoice, and verify the current pricing and carrier quote before trying again."
          onConfirm={approveReview}
          onClose={() => setSelectedReview(null)}
        />
      ) : null}
    </Card>
  );
}
